import { ControlProps } from '@jsonforms/core';
import { delay, get, intersectionWith } from 'lodash';
import { FunctionComponent, useEffect, useRef } from 'react';
import { setSchemasFieldErrors } from '../../../../../utils/DynamicForms';
import { Tooltip } from '../../../../Tooltip';
import { useDynamicFormContext } from '../../../DynamicFormContext';
import './style.scss';

export type FieldProps = Omit<ControlProps, 'enabled'> & {
  handleBlur: (path: string) => void;
  handleRemove: (path: string) => void;
  handleFocused?: () => void;
  handleChange: (path: string, value: any, hasNoValidation?: boolean) => void;
  formData: any;
  disabled: boolean;
  optionalMessage?: string;
};

export const WithDynamicFormContext = (
  Control: FunctionComponent<FieldProps>,
) => {
  const ComponentToReturn = (props: ControlProps) => {
    const updatedPath = props?.path?.replaceAll?.('then.', '');
    const initialRenderRef = useRef(true);
    const options = props.uischema.options;

    const {
      formData,
      errors,
      uiSchema,
      handleFieldChange,
      handleFieldRemove,
      handleMutateChanges,
      handleBlur,
    } = useDynamicFormContext();

    const fieldData = get(formData, updatedPath);
    const fieldError = setSchemasFieldErrors(errors, updatedPath);
    const showError = options?.hideError && fieldError?.keyword !== 'required';
    //@ts-ignore
    const optionalMsg = uiSchema?.options?.optionalMessage;

    useEffect(() => {
      if (initialRenderRef.current) {
        initialRenderRef.current = false;
        return;
      }

      if (!props.visible && fieldData === undefined) return;

      if (!props.visible && fieldData !== undefined) {
        if (options?.removeDefaults) {
          if (!options?.resetValue) return;
          if (
            get(formData, options?.resetValue?.dataPath) ===
            options?.resetValue?.show?.value
          )
            return;

          const validValue = intersectionWith(
            get(formData, updatedPath),
            options?.resetValue?.show?.list,
          );

          handleFieldChange(updatedPath, validValue);
        } else {
          if (updatedPath.includes('Deductible')) return;
          handleFieldRemove(updatedPath);
        }
      }
      if (props.visible && props.schema.default !== undefined) {
        if (options?.removeDefaults) {
          if (!options?.resetValue) return;
          if (
            get(formData, options?.resetValue?.dataPath) ===
            options?.resetValue?.show?.value
          )
            return;

          const validValue = intersectionWith(
            get(formData, updatedPath),
            options?.resetValue?.show?.list,
          );

          handleFieldChange(updatedPath, validValue);
        } else {
          delay(() => {
            handleFieldChange(updatedPath, props.schema.default);
          }, 0);
          handleMutateChanges([{ path: updatedPath, value: props.schema.default }])
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.visible]);

    if (!props.visible) return null;

    const disabled = props.uischema.options?.disabled;
    const tooltip = props.uischema.options?.tooltip;

    const ControlElement = (
      <Control
        {...props}
        path={updatedPath}
        data={fieldData}
        handleChange={handleFieldChange}
        handleRemove={handleFieldRemove}
        handleBlur={handleBlur}
        formData={formData}
        disabled={disabled || !props.enabled}
        optionalMessage={optionalMsg}
      />
    );

    return (
      <div className={`control-wrapper ${(fieldError && !showError) ? 'input-has-error' : ''} ${options?.hidden ? 'hidden-control' : ''}`}>
        {!!tooltip && (
          <Tooltip title={tooltip.title} position={tooltip.position}>
            {ControlElement}
          </Tooltip>
        )}

        {!tooltip && ControlElement}

        <div className="field-error-wrapper">
          {(fieldError && !showError) && <p className="tru-errors">{fieldError.message}</p>}
        </div>
      </div>
    );
  };

  return ComponentToReturn;
};
